import type { Theme } from "@mui/material";
import {
  Box,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { DIALOG_WIDTH, STATUS_COLOR } from "../../constants";
import StandardDialogTitle from "./StandardDialogTitle";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Schemas } from "../../schemas";
import StandardDialogActions from "./StandardDialogActions";
import z from "zod";
import * as React from "react";
import { CloseDialogHandler } from "../../contexts/DialogContext";
import { useCreateAlertEvent } from "../../hooks/mutation/create/useCreateAlertEvent";

const schema = Schemas.alertEvent.omit({ id: true, timestamp: true }).extend({count: z.string()});

const NewAlertEventDialog: React.FC<{
  alertId: string,
  onDialogClosed: CloseDialogHandler
}> = ({ alertId, onDialogClosed }) => {
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const { mutateAsync: createAlertEvent, isPending } = useCreateAlertEvent(alertId);

  const [submitError, setSubmitError] = React.useState<string | null>(null);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { alertId },
    mode: "onBlur",
  });

  const onSubmit = async (data: z.infer<typeof schema>) => {
    try {
      setSubmitError(null);
      await createAlertEvent(data);
      reset({});
      onDialogClosed({
          message: "Alert event created!",
          severity: "success"
      });
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ??
        err?.message ??
        "Something went wrong while creating the alert.";
      setSubmitError(String(msg));
    }
  };

  const onClose = () => {
    setSubmitError(null);
    reset({});
    onDialogClosed();
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      fullWidth
      fullScreen={isSmallScreen}
      PaperProps={{ sx: { maxWidth: isSmallScreen ? undefined : DIALOG_WIDTH.lg } }}
    >
      <StandardDialogTitle title={"New Alert Event"} isSmallScreen={isSmallScreen} onClose={onClose} />

      <Box
        component="form"
        id="new-alert-form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <DialogContent sx={{ overflowX: "hidden" }}>
          <Box sx={{ width: "100%" }}>
            <Stack sx={{ mb: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                Create an alert event.
              </Typography>
            </Stack>

            <Divider sx={{ mb: 1.5 }} />

            <Stack spacing={2.25} sx={{ width: "100%" }}>

               <FormControl error={!!errors.status}>
                <FormLabel>Status</FormLabel>

                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <Select
                      size="small"
                      value={field.value ?? ""}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                        if (submitError) {setSubmitError(null);}
                      }}
                      displayEmpty
                    >
                      <MenuItem value="">
                        <em>Select a status</em>
                      </MenuItem>

                      {Object.keys(STATUS_COLOR)?.map((status) => (
                        <MenuItem value={status} key={status}>
                          {status}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />

                <FormHelperText>
                  {errors.status?.message ?? " "}
                </FormHelperText>
              </FormControl>

              <TextField
                  size="medium"
                  multiline
                  placeholder="Your payload"
                  error={!!errors.metadata}
                  {...register("metadata", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                />
                <FormHelperText error={!!errors.metadata}>
                  {errors.metadata?.message ?? " "}
                </FormHelperText>

                <TextField
                  size="small"
                  type="number"
                  placeholder="Duplicates count"
                  error={!!errors.count}
                  {...register("count", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                />
                <FormHelperText error={!!errors.count}>
                  {errors.count?.message ?? " "}
                </FormHelperText>
              
              {submitError && (
                <FormHelperText error sx={{ fontSize: 13, m: 0 }}>
                  {submitError}
                </FormHelperText>
              )}
            </Stack>
          </Box>
        </DialogContent>

        <StandardDialogActions
          primaryText="Create"
          primaryIcon={<></>}
          onCancel={onClose}
          loading={isPending}
          disabled={isPending}
        />
      </Box>
    </Dialog>
  );
};

export default NewAlertEventDialog;
